const LOW_STOCK_THRESHOLD = 5;

const parseVariation = (product) => {
    if (!product?.variation) return [];
    if (Array.isArray(product.variation)) return product.variation;
    if (typeof product.variation === "string") {
        try {
            const parsed = JSON.parse(product.variation);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const stockToken = (stock) => (stock ?? "").toString().trim().toLowerCase();

const isUnlimitedStock = (val) =>
    val.includes("unlimited") || val === "unlimited stock";

const isOutOfStockStatus = (val) =>
    val === "out of stock" || val === "outofstock" || val === "sold out";

const isInStockStatus = (val) =>
    val === "in stock" || val === "instock" || val === "available";

export const isActiveProduct = (product) =>
    String(product?.status ?? "")
        .trim()
        .toLowerCase() === "active";

const isVariableProduct = (product) =>
    String(product?.product_type ?? "")
        .trim()
        .toLowerCase() === "variable product";

/** Actual quantity — stored in total / totalStock, NOT in stock (status label) */
const parsePositiveInt = (raw) => {
    if (raw == null || raw === "" || raw === "—" || raw === "-") return null;
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const getParentQuantity = (product) =>
    parsePositiveInt(product?.total ?? product?.totalStock ?? product?.quantity);

const getStockStatus = (product) => stockToken(product?.stock);

/**
 * stock  = status label only ("In stock", "Out of stock", "Unlimited Stock")
 * total  = numeric quantity (when not unlimited)
 */
const isSimpleLikeInStock = (product) => {
    const status = getStockStatus(product);

    if (isOutOfStockStatus(status)) return false;
    if (isUnlimitedStock(status)) return true;
    if (isInStockStatus(status)) return getParentQuantity(product) !== null;

    // No status label — fall back to quantity if present
    return getParentQuantity(product) !== null;
};

const getVariantQuantity = (variant) =>
    parsePositiveInt(variant?.stock ?? variant?.total ?? variant?.totalStock);

const isVariableInStock = (product) => {
    const parentStatus = getStockStatus(product);

    if (isOutOfStockStatus(parentStatus)) return false;
    if (isUnlimitedStock(parentStatus)) return true;

    const variants = parseVariation(product);
    if (variants.some((v) => getVariantQuantity(v) !== null)) return true;

    if (isInStockStatus(parentStatus)) return getParentQuantity(product) !== null;

    return getParentQuantity(product) !== null;
};

export const isInStock = (product) => {
    if (isVariableProduct(product)) return isVariableInStock(product);
    return isSimpleLikeInStock(product);
};

export const isOutOfStock = (product) => !isInStock(product);

const getQuantitiesForLowStock = (product) => {
    const quantities = [];

    if (isVariableProduct(product)) {
        const parentStatus = getStockStatus(product);
        if (isUnlimitedStock(parentStatus)) return [];

        parseVariation(product).forEach((variant) => {
            const qty = getVariantQuantity(variant);
            if (qty !== null) quantities.push(qty);
        });

        if (quantities.length === 0) {
            const parentQty = getParentQuantity(product);
            if (parentQty !== null) quantities.push(parentQty);
        }
        return quantities;
    }

    const status = getStockStatus(product);
    if (isUnlimitedStock(status)) return [];

    const qty = getParentQuantity(product);
    if (qty !== null) quantities.push(qty);

    return quantities;
};

export const isLowStock = (product, threshold = LOW_STOCK_THRESHOLD) => {
    if (!isInStock(product)) return false;
    return getQuantitiesForLowStock(product).some((n) => n <= threshold);
};

export const isAddedThisMonth = (product) => {
    const raw = product?.createdAt ?? product?.created_at;
    if (!raw) return false;
    const created = new Date(raw);
    if (Number.isNaN(created.getTime())) return false;
    const now = new Date();
    return (
        created.getFullYear() === now.getFullYear() &&
        created.getMonth() === now.getMonth()
    );
};

export const computeProductMetrics = (products = []) => {
    const list = Array.isArray(products) ? products : [];
    let active = 0;
    let inactive = 0;
    let inStock = 0;
    let outOfStock = 0;
    let lowStock = 0;
    let addedThisMonth = 0;

    list.forEach((product) => {
        if (isActiveProduct(product)) active += 1;
        else inactive += 1;

        if (isInStock(product)) inStock += 1;
        else outOfStock += 1;

        if (isLowStock(product)) lowStock += 1;
        if (isAddedThisMonth(product)) addedThisMonth += 1;
    });

    return {
        total: list.length,
        active,
        inactive,
        inStock,
        outOfStock,
        lowStock,
        addedThisMonth,
    };
};

export const matchesMetricFilter = (product, metricFilter) => {
    switch (metricFilter) {
        case "active":
            return isActiveProduct(product);
        case "inactive":
            return !isActiveProduct(product);
        case "inStock":
            return isInStock(product);
        case "outOfStock":
            return isOutOfStock(product);
        case "lowStock":
            return isLowStock(product);
        case "addedThisMonth":
            return isAddedThisMonth(product);
        default:
            return true;
    }
};

export const productMatchesGlobalSearch = (product, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;

    const haystack = [
        product?.title,
        product?.sku,
        product?.category,
        product?.category_details?.category,
        product?.product_type,
        product?.status,
        product?.stock,
        product?.total,
        product?.totalStock,
        product?.originalPrice,
        product?.discountPrice,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (haystack.includes(q)) return true;

    if (product?.variation) {
        try {
            return JSON.stringify(product.variation).toLowerCase().includes(q);
        } catch {
            return false;
        }
    }

    return false;
};
